import React, { useEffect, useState } from 'react';
import { encodeBase64, decodeBase64 } from '../base64';
import { useNavigate, Link } from 'react-router-dom';
import EventForm from './EventForm';
import { displayEventDate } from '../dateUtils';
import Seo from './Seo';
import {
  createHomeStructuredData,
  eventSlug,
  SITE_DESCRIPTION,
  SITE_NAME,
  SITE_URL,
  toAbsoluteUrl
} from '../seo';

const owner = process.env.REACT_APP_GITHUB_OWNER;
const repo = process.env.REACT_APP_GITHUB_REPO;
const branch = process.env.REACT_APP_GITHUB_BRANCH || 'main';
const token = process.env.REACT_APP_GITHUB_TOKEN;
const eventsUrl = `https://raw.githubusercontent.com/${owner}/${repo}/${branch}/events.json`;

async function getFile(path) {
  const res = await fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${path}?ref=${branch}`, {
    headers: { Authorization: `token ${token}` }
  });
  if (!res.ok) return null;
  return res.json();
}

async function updateFile(path, content, message, sha) {
  return fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${path}`, {
    method: 'PUT',
    headers: {
      Authorization: `token ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ message, content, branch, sha })
  });
}

async function deleteFile(path, message, sha) {
  return fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${path}`, {
    method: 'DELETE',
    headers: {
      Authorization: `token ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ message, sha, branch })
  });
}

function Home() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editEvent, setEditEvent] = useState(null);

  const loggedIn = localStorage.getItem('loggedIn') === 'true';
  const visibleEvents = loggedIn ? events : events.filter((e) => !e.draft);
  const featuredImage = toAbsoluteUrl(visibleEvents.find((event) => event.imageUrl)?.imageUrl);

  useEffect(() => {
    async function loadEvents() {
      try {
        const apiRes = await fetch(
          `https://api.github.com/repos/${owner}/${repo}/contents/events.json?ref=${branch}`,
          {
            headers: token ? { Authorization: `token ${token}` } : {}
          }
        );
        let data = [];
        if (apiRes.ok) {
          const json = await apiRes.json();
          data = JSON.parse(decodeBase64(json.content));
        } else {
          const rawRes = await fetch(eventsUrl);
          if (rawRes.ok) {
            data = await rawRes.json();
          }
        }
        const sorted = (data || []).sort((a, b) => new Date(b.date) - new Date(a.date));
        setEvents(sorted);
      } catch {
        setEvents([]);
      } finally {
        setLoading(false);
      }
    }
    loadEvents();
  }, [showForm]);

  const handleEdit = (evt) => {
    setEditEvent(evt);
    setShowForm(true);
  };

  const handleDelete = async (evt) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      const file = await getFile('events.json');
      if (!file) return;
      const eventsData = JSON.parse(decodeBase64(file.content));
      const updated = eventsData.filter((e) => e.id !== evt.id);
      await updateFile('events.json', encodeBase64(JSON.stringify(updated, null, 2)), `Delete event ${evt.id}`, file.sha);

      if (evt.imageUrl) {
        const imgPath = `public${evt.imageUrl}`;
        const imgFile = await getFile(imgPath);
        if (imgFile) {
          await deleteFile(imgPath, `Delete image ${evt.id}`, imgFile.sha);
        }
      }

      setEvents(updated);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <div className="p-10">Loading...</div>;

  return (
    <div className="pt-10 pb-40 mx-5 md:mx-[10vw]">
      <Seo
        title={`${SITE_NAME} | Artist-run art space in London`}
        description={SITE_DESCRIPTION}
        canonicalUrl={`${SITE_URL}/`}
        imageUrl={featuredImage}
        structuredData={createHomeStructuredData(featuredImage)}
      />
      {loggedIn && (
        <div className="fixed top-4 right-4 flex gap-2 z-30">
          <button
            className="bg-black text-white p-2"
            onClick={() => {
              setEditEvent(null);
              setShowForm(true);
            }}
          >
            Add Event
          </button>
          <button
            className="bg-black text-white p-2"
            onClick={() => {
              localStorage.removeItem('loggedIn');
              navigate('/login');
            }}
          >
            Logout
          </button>
        </div>
      )}
      {showForm && (
        <EventForm
          existing={editEvent}
          onClose={() => {setShowForm(false); setEditEvent(null);}}
        />
      )}
      <div className="flex flex-col gap-10">
        {visibleEvents.map((evt) => (
          <div key={evt.id}>
            <Link to={`/events/${eventSlug(evt)}/`}>
              <img src={evt.imageUrl} alt={evt.title} className="w-full h-auto mb-4" />
            </Link>
            <h2 className="text-2xl font-bold mb-1">
              <Link to={`/events/${eventSlug(evt)}/`}>{evt.title}</Link>
            </h2>
            <div className="mb-2 text-gray-600">{displayEventDate(evt.date, evt.endDate)}</div>
            <p className="whitespace-pre-wrap leading-relaxed">{evt.description}</p>
            {loggedIn && (
              <div className="mt-2 text-sm flex gap-2">
                <button className="underline" onClick={() => handleEdit(evt)}>Edit</button>
                <button className="underline text-red-600" onClick={() => handleDelete(evt)}>Delete</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Home;
